<template lang="pug">
.table-settings
	swiper.swipers(
		:class='{ active : activeSlide > 0 }'
		:options='swiperConfig'
		:active-slide='activeSlide'
		@swiper='swiper = $event'
		@slide-change-transition-end='close'
	)
		.empty(
			slot='slide-1'
			@click='cancel'
		)
		.page(
			slot='slide-2'
			:class='state'
		)
			.page-content
				.header
					h2 {{ name }} Table Settings
					.icon-close(@click='close')
				.scroll-container
					p.info Click any of the eye icons to hide a column.
					.settings
						.setting(v-for='( column, key ) in columns')
							p.text {{ column.title }}
							.icon(
								@click='toggleSetting( key )'
								v-if='updatedSettings[key]'
								:class='updatedSettings[key].hidden ? "icon-eye-blocked" : "icon-eye"'
							)
				.buttons(:class='{ unsaved }')
					.button.save(@click='saveTableSettings( updatedSettings )') Save
					.button.cancel(@click='close') Cancel
</template>

<script>
import Swiper from '@/components/Swiper';

// eslint-disable-next-line no-unused-vars
import Vue from 'vue';
import { GetTableSettings, SaveTableSettings } from '@/lib/db/tableSettings';
import { clone } from '@/lib/utils';

export default {
	name : 'table-settings',

	props : {
		name : {
			type : String
		},
		columns : {
			required : true,
			type     : Object,
		},
		state : {
			required : true,
			type     : String
		},
	},

	data : () => ( {
		updatedSettings  : {},
		originalSettings : {},
		unsaved          : false,

		swiper       : null,
		swiperConfig : {
			numberOfSlides : 2,
			config         : {
				// auto initialize this swiper
				init         : true,
				initialSlide : 0,

				// swiper settings
				slidesPerView   : document.body.clientWidth > 500 ? 'auto' : 1, // don't force all slides to be 100vw
				speed           : 400, // ms for when you let go of swipe
				spaceBetween    : 0, // no space between
				resistanceRatio : 0, // stops you from swiping past all slides
				direction       : 'horizontal',
				simulateTouch	  : false,
				threshold       : 50, // min distance for scroll to work
			}
		},

		activeSlide : 0,
	} ),

	computed : {

		user() {
			return this.$store.state.user;
		},

	},

	watch : {

		user : {
			immediate : true,
			handler( user ) {

				// initialize the table settings when the
				// user is defined. This funciton should only
				// be called once as it is a listener for
				// firestore
				if ( user && user.uid ) {
					this.getTableSettings( user.uid );
				}

			}
		},

		originalSettings( settings ) {
			// emit the settings up and reset our updated
			// settings to be the same as our current settings.
			// + This function will fire every time that the
			// + settings are saved.
			this.$emit( 'settings', clone( settings ) );
			this.updatedSettings = clone( settings );
		},

		state : {
			immediate : true,
			handler( state ) {

				if ( state === 'close' ) {
					this.cancel();
					return;
				}

				this.open();
			},

		},

		activeSlide( activeSlide ) {
			if ( activeSlide === 0 ) {
				this.cancel();
			}
		}

	},

	methods : {

		toggleSetting( columnKey ) {
			this.updatedSettings[columnKey].hidden = !this.updatedSettings[columnKey].hidden;
			this.unsaved = true;
		},

		cancel() {
			this.updatedSettings = clone( this.originalSettings );

			this.close();
		},

		open() {
			this.activeSlide = 1;
			this.$emit( 'open' );
		},

		close() {
			this.activeSlide = 0;
			this.$emit( 'close' );
		},

		getTableSettings( uid ) {

			// + Create a firebase listener
			GetTableSettings( uid, this.name, ( settings ) => {

				if ( !settings ) {
					const colKeys  = Object.keys( this.columns );
					const set = colKeys.reduce( ( obj, key ) => {
						// eslint-disable-next-line no-param-reassign
						obj[key] = {
							hidden : false,
						};

						return obj;
					}, {} );

					this.saveTableSettings( set );

					return;
				}

				// + This will fire every single time that
				// + the firebase document changes.
				this.originalSettings = settings;
			} );

		},

		saveTableSettings( settings ) {

			// If there's no user, just return
			if ( !this.user ) {
				return;
			}

			const { uid } = this.user;

			SaveTableSettings( uid, this.name, settings );
			this.unsaved = false;

		}

	},

	components : {
		Swiper
	}

};
</script>

<style lang="scss">
.table-settings {

	.swipers {
		position: fixed;
		top: 0;
		left: 0;
		height: 100vh;
		width: 100vw;
		z-index: 100;
		pointer-events: none;

		&.active {
			pointer-events: all;

			.empty {
				opacity: 1 !important;
			}
		}

		.swiper-wrapper .swiper-slide {

			&:not( :nth-child( 1 ) ) {
				width: 400px;
			}

			> .empty {
				opacity: 0;
				width: 100vw;
				height: 100vh;
				background: $opaqueDarkBlue;
			}

			> .page {
				background: white;
				height: 100%;
				overflow-y: auto;
				padding: 30px 0;

				&.open {
					box-shadow: $box-shadow-primary;
				}

				.header {
					display: flex;
					align-items: center;
					justify-content: space-between;
					padding: 15px 20px;
					color : $color-primary;
					text-transform: capitalize;
				}
			}
		}
	}

	.scroll-container {
		overflow : auto;

		.info {
			margin : 10px 20px;
		}

		.settings {

			.setting {
				display: flex;
				align-items: center;
				justify-content: space-between;
				padding: 10px 20px;

				.icon {
					cursor: pointer;
					font-size: 22px;
					color: $text-primary;
				}
			}
		}
	}

	.buttons {
		display: flex;
		margin: 20px;
		justify-content: center;

		&:not(.unsaved) {

			.button {
				&.save {
					pointer-events: none;
					filter: grayscale(1);
				}
			}
		}

		.button {
			margin: 10px;

			&.save {
				filter: grayscale(0);
				transition: filter .2s ease;
			}

			&.cancel {
				background: white;
				color: $text-primary;
				box-shadow: none;
			}
		}
	}
}
</style>
